<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import OrganizationStorageInfo from '@/components/organizations/OrganizationStorageInfo.vue';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import { useGlobalStore } from '@/stores/global';
import { useOrganizationStore } from '@/stores/organization';
import type { OrganizationEmployee } from '@/types/employee';
import type { Organization } from '@/types/organization';
import toast from '@/utils/toast';
import { required } from '@/utils/validation';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';

const organizationIdFromUrl = computed(() => route.params.organizationId?.toString() ?? '');

const organizationStore = useOrganizationStore();
const employeeStore = useEmployeeStore();
const globalStore = useGlobalStore();
const authStore = useAuthStore();

const organization = ref<Organization | null>();
const organizationEmployees = ref<OrganizationEmployee[]>([]); // TODO: save in like an organizationStore or something pls, instead of having to fetch this in a bunch of places.

const route = useRoute();

const tempOrganizationName = ref('');
const form = useTemplateRef('form');
const isEditing = ref(false);
const isInitializing = ref(true);

async function fetchOrganizationInformation(): Promise<void> {
    organization.value = await organizationStore.fetchById(organizationIdFromUrl.value);
    organizationEmployees.value = await employeeStore.fetchInOrganization(organizationIdFromUrl.value);
}

onMounted(async () => {
    await fetchOrganizationInformation();
    isInitializing.value = false;
});

watch(organizationIdFromUrl, async () => {
    await fetchOrganizationInformation();
});

function startEditingName() {
    if (authStore.isSuperAdmin()) return router.push({ name: 'organization-edit', params: { organizationId: organization.value?._id } });

    tempOrganizationName.value = organization.value?.name ?? '';
    isEditing.value = true;
}

async function saveOrganization() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    if (!organization.value) {
        toast.error('OrganizationUpdateFailed');
        return;
    }

    if (!tempOrganizationName.value) {
        toast.error('OrganizationNameRequired');
        return;
    }

    organization.value.name = tempOrganizationName.value;
    const response = await organizationStore.update(organization.value);

    if (response.status !== 200) {
        toast.error('OrganizationUpdateFailed');
        return;
    }

    isEditing.value = false;
    toast.success('OrganizationUpdated');

    return;
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <template v-if="organization">
                <v-row class="align-center ga-2 mb-10 w-md-50 w-100">
                    <v-icon icon="business" class="text-main-darker" />
                    <div class="d-flex align-center" v-if="!isEditing">
                        <h3 class="my-0 py-0 mr-2">{{ organization.name }}</h3>
                        <v-icon icon="edit" class="text-alt cursor-pointer font-small" @click="startEditingName()" v-if="!authStore.isReadOnly()" />
                    </div>
                    <div class="d-flex flex-grow-1 align-center" v-else>
                        <v-form ref="form" class="mr-5 flex-grow-1">
                            <v-text-field
                                autofocus
                                class="py-0 my-0"
                                density="compact"
                                hide-details
                                v-model="tempOrganizationName"
                                :rules="[required]"
                                required
                            />
                        </v-form>
                        <CustomButton class="my-0 mr-2" @click.stop="saveOrganization()" text="Opslaan" />
                        <CustomButton class="my-0" color="red" @click.stop="isEditing = false" text="Annuleren" />
                    </div>
                </v-row>
                <v-card max-width="300" class="m-0">
                    <v-list>
                        <v-list-item title="Dashboard" :to="`/dashboard/${organization._id}`" prepend-icon="dashboard" class="hover-main" />
                        <v-list-item title="Actielijst" :to="`/actielijst/org/${organization._id}`" prepend-icon="list" class="hover-main" />
                        <v-list-item title="Medewerkers" :to="`/medewerkers/${organization._id}`" prepend-icon="group" class="hover-main" />
                        <v-list-item
                            title="Vestigingen"
                            :to="`/bedrijf/${organization._id}/vestigingen`"
                            prepend-icon="business"
                            class="hover-main"
                        />
                        <v-list-item
                            title="Bronnen"
                            :to="{ name: 'organization-sources', params: { organizationId: organization._id } }"
                            prepend-icon="source"
                            class="hover-main"
                        />
                        <v-list-item prepend-icon="storage" v-if="!organization.parent">
                            <OrganizationStorageInfo
                                formFactor="span"
                                :currentUploadSizeMb="organization.currentUploadSizeMb"
                                :maxUploadSizeMb="organization.maxUploadSizeMb"
                                :progressBarHeight="5"
                            />
                        </v-list-item>
                    </v-list>
                </v-card>
            </template>
            <template v-else>Bedrijf niet gevonden</template>
        </div>
    </v-fade-transition>
</template>

<style scoped>
label {
    font-weight: bold;
    font-size: 18px;
}

span {
    font-size: 18px;
}

.v-list-item {
    border-bottom: 1px solid lightgray;
}

.v-list:deep() .v-icon {
    opacity: 0.9;
}
.v-list {
    padding: 0;
}
</style>
